'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';

export type IStepActionStatus = 'idle' | 'running' | 'done' | 'error';

interface IUseStepActionParams {
  start: () => Promise<Response>;
}

interface IUseStepActionReturn {
  status: IStepActionStatus;
  log: string;
  run: () => Promise<void>;
  reset: () => void;
}

async function readStream(body: ReadableStream<Uint8Array>, append: (text: string) => void) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      return;
    }
    append(decoder.decode(value, { stream: true }));
  }
}

export function useStepAction({ start }: IUseStepActionParams): IUseStepActionReturn {
  const router = useRouter();
  const [status, setStatus] = useState<IStepActionStatus>('idle');
  const [log, setLog] = useState('');

  const run = useCallback(async () => {
    setStatus('running');
    setLog('');
    try {
      const res = await start();
      if (!res.body) {
        setLog((prev) => `${prev}[error] no response stream\n`);
        setStatus('error');
        return;
      }
      await readStream(res.body, (text) => setLog((prev) => prev + text));
      setStatus('done');
      router.refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setLog((prev) => `${prev}\n[error] ${message}\n`);
      setStatus('error');
    }
  }, [start, router]);

  const reset = useCallback(() => {
    setStatus('idle');
    setLog('');
  }, []);

  return { status, log, run, reset };
}
